import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import TableRow from '@mui/material/TableRow';
import { visuallyHidden } from '@mui/utils';
import { AxiosResponse } from 'axios';
import ButtonIcon from 'components/ButtonIcon';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import GenericDialog from 'components/GenericDialog';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import UseAuthContext from 'context/AuthContext';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis
} from 'recharts';
import {
  getAdminOrganizationListsOptimized,
  getOrganizationListsCSV
} from 'services/admin-organizations';
import { axiosInstance } from 'utils/AxiosInstance';
import { API_PATHS } from 'utils/constants';
import { IErrorResponse } from 'utils/contracts';
import {
  getErrorHtmlContent
} from 'utils/utils';
import OrganizationAutocompleteFilter from './OrganizationAutocompleteFilter';

const TableRowWithHover = ({ index, item, handleViewOrganizationPage, setOrganization, navigate, data, t, setAnchorElMenu, setMenuOrgId, menuOrgId, anchorElMenu, handleCloseMenu, deleteOrganizationConfirmation }) => {
  const [hover, setHover] = useState(false);

  // Helper function to format progression percentage
  const formatProgression = (percentage: number) => {
    if (percentage === 0) return '0%';
    if (percentage > 0) return `+${percentage.toFixed(1)}%`;
    return `${percentage.toFixed(1)}%`;
  };

  // Helper function to get progression color
  const getProgressionColor = (percentage: number) => {
    if (percentage > 0) return '#4caf50'; // Green
    if (percentage < 0) return '#f44336'; // Red
    return '#757575'; // Gray
  };

  return (
    <TableRow
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        "&:last-child td, &:last-child th": { border: 0 },
        cursor: 'pointer',
      }}
      key={item.id}
    >
      <CustomTableCell onClick={(e) => handleViewOrganizationPage(e, item)} width={"60px"}>
        {item.shortId}
      </CustomTableCell>
      <CustomTableCell onClick={(e) => handleViewOrganizationPage(e, item)} style={{ width: "250px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
          }}
        >
          {item.image_url ? (
            <img src={item.image_url} width="30px" />
          ) : null}
          {item.company_name}
        </Box>
      </CustomTableCell>
      {/* Project Progression Column */}
      <CustomTableCell onClick={(e) => handleViewOrganizationPage(e, item)} style={{ width: "200px" }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <span style={{ 
            color: getProgressionColor(item.projectProgression.progressionPercentage),
            fontWeight: 'bold',
            direction: 'ltr'
          }}>
            {formatProgression(item.projectProgression.progressionPercentage)}
          </span>
          <span style={{ fontSize: '0.75rem', color: '#666' }}>
            {item.projectProgression.currentPeriodCount} / {item.projectProgression.previousPeriodCount}
          </span>
        </Box>
      </CustomTableCell>
      {/* Revision Progression Column */}
      <CustomTableCell onClick={(e) => handleViewOrganizationPage(e, item)} style={{ width: "200px" }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <span style={{ 
            color: getProgressionColor(item.revisionProgression.progressionPercentage),
            fontWeight: 'bold',
            direction: 'ltr'
          }}>
            {formatProgression(item.revisionProgression.progressionPercentage)}
          </span>
          <span style={{ fontSize: '0.75rem', color: '#666' }}>
            {item.revisionProgression.currentPeriodCount} / {item.revisionProgression.previousPeriodCount}
          </span>
        </Box>
      </CustomTableCell>
      {/* Transaction Progression Column */}
      <CustomTableCell onClick={(e) => handleViewOrganizationPage(e, item)} style={{ width: "200px" }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <span style={{ 
            color: getProgressionColor(item.transactionProgression.progressionPercentage),
            fontWeight: 'bold',
            direction: 'ltr'
          }}>
            {formatProgression(item.transactionProgression.progressionPercentage)}
          </span>
          <span style={{ fontSize: '0.75rem', color: '#666' }} >
            {item.transactionProgression.currentPeriodCount} / {item.transactionProgression.previousPeriodCount}
          </span>
        </Box>
      </CustomTableCell>
      {/* Activity Status Column */}
      <CustomTableCell onClick={(e) => handleViewOrganizationPage(e, item)} style={{ width: "150px" }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <span style={{ 
            color: item.hasRecentActivity ? '#4caf50' : '#f44336',
            fontWeight: 'bold',
            fontSize: '0.875rem'
          }}>
          {item.daysSinceLastActivity > 0 ? `${item.daysSinceLastActivity} ${t('ADMIN_STATS_DAYS_AGO')}` : ''}
          </span>
       
        </Box>
      </CustomTableCell>
      <CustomTableCell width={"100px"} onClick={(e) => handleViewOrganizationPage(e, item)}>
        <div style={{ display: "flex", gap: "10px" }}>
          <span className="data">
            {moment
              .utc(item.updatedAt)
              .local()
              .format("DD/MM/YY")}
          </span>
          <span className="time" >
            {moment.utc(item.updatedAt).local().format("HH:mm")}
          </span>
        </div>
      </CustomTableCell>
      <CustomTableCell width={"100px"} align="right">
        <ButtonIcon
          isActive={menuOrgId === item.id}
          aria-label='more'
          aria-controls='long-menu'
          aria-haspopup='true'
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            setAnchorElMenu(event.currentTarget);
            setMenuOrgId(item.id);
          }}
        >
          <MoreHorizIcon fontSize='medium' />
        </ButtonIcon>
        <Menu
          id={`menu-${item.id}`}
          open={menuOrgId === item.id}
          anchorEl={anchorElMenu}
          onClose={handleCloseMenu}
          sx={{ fontSize: "14px" }}
        >
          <div>
            <MenuItem onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/edit/business/${item.id}`);
            }}>{t('BUSINESS_MENU_EDIT')}</MenuItem>
            <MenuItem onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOrganization(
                data.find((x: any) => x.id == item.id)
              );
              navigate(`/admin/organization/${item.id}`);
            }}>{t('BUSINESS_MENU_VIEW_DETAILS')}</MenuItem>
            <MenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteOrganizationConfirmation(item);
              }}>{t('BUSINESS_MENU_DELETE')}</MenuItem></div>
        </Menu>
      </CustomTableCell>
    </TableRow>
  );
};

// Chart component to display organization progression
interface OrganizationProgressionChartProps {
  data: any[];
  chartType: 'project' | 'revision' | 'transaction';
  t: (key: string) => string;
  isLoading: boolean;
}

const OrganizationProgressionChart: React.FC<OrganizationProgressionChartProps> = ({ data, chartType, t, isLoading }) => {
  const [visualizationType, setVisualizationType] = useState<'line' | 'bar' | 'scatter'>('bar');
  const theme = useTheme();

  const isRTL = theme.direction === 'rtl';

  // Prepare data for chart
  const prepareChartData = () => {
    if (!data || !data.length) return [];
    
    return data.map((org, index) => {
      let progressionData: any = {};
      
      switch (chartType) {
        case 'project':
          progressionData = {
            name: org.company_name,
            progression: org.projectProgression.progressionPercentage,
            current: org.projectProgression.currentPeriodCount,
            previous: org.projectProgression.previousPeriodCount,
            image_url: org.image_url
          };
          break;
        case 'revision':
          progressionData = {
            name: org.company_name,
            progression: org.revisionProgression.progressionPercentage,
            current: org.revisionProgression.currentPeriodCount,
            previous: org.revisionProgression.previousPeriodCount,
            image_url: org.image_url
          };
          break;
        case 'transaction':
          progressionData = {
            name: org.company_name,
            progression: org.transactionProgression.progressionPercentage,
            current: org.transactionProgression.currentPeriodCount,
            previous: org.transactionProgression.previousPeriodCount,
            image_url: org.image_url
          };
          break;
        default:
          progressionData = {
            name: org.company_name,
            progression: org.projectProgression.progressionPercentage,
            current: org.projectProgression.currentPeriodCount,
            previous: org.projectProgression.previousPeriodCount,
            image_url: org.image_url
          };
      }
      
      return {
        ...progressionData,
        index: index + 1
      };
    })
    .filter(item => item.progression !== 0) // Filter out 0% progression
    .sort((a, b) => b.progression - a.progression); // Sort by progression descending
  };

  const chartData = prepareChartData();

  const getChartTitle = () => {
    switch (chartType) {
      case 'project':
        return t('ADMIN_STATS_PROJECT_PROGRESSION');
      case 'revision':
        return t('ADMIN_STATS_REVISION_PROGRESSION');
      case 'transaction':
        return t('ADMIN_STATS_CREDITS_PROGRESSION');
      default:
        return t('ADMIN_STATS_PROJECT_PROGRESSION');
    }
  };

  const getChartColor = () => {
    switch (chartType) {
      case 'project':
        return '#ed6c02';
      case 'revision':
        return '#9c27b0';
      case 'transaction':
        return '#d32f2f';
      default:
        return '#ed6c02';
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const progression = data.progression;
      const isPositive = progression > 0;
      const isNegative = progression < 0;
      
      return (
        <Box sx={{ 
          backgroundColor: 'white', 
          border: '1px solid #ccc', 
          p: 2, 
          borderRadius: 2,
          boxShadow: 3,
          minWidth: 200
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {data.image_url && (
              <img 
                src={data.image_url} 
                alt={data.name}
                style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '6px',
                  objectFit: 'cover',
                  border: '2px solid #f0f0f0'
                }} 
              />
            )}
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
              {data.name}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: isPositive ? 'rgba(76, 175, 80, 0.1)' : 
                           isNegative ? 'rgba(244, 67, 54, 0.1)' : 'rgba(117, 117, 117, 0.1)'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: isPositive ? '#4caf50' : isNegative ? '#f44336' : '#757575',
                fontSize: '1.1rem'
              }}
              dir= 'ltr'

            >
              {progression > 0 ? '+' : ''}{progression.toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              progression
            </Typography>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Current Period
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.current}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Previous Period
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.previous}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              ID: {data.shortId || data.id}
            </Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  // Function to get dot color based on progression
  const getDotColor = (progression: number) => {
    if (progression > 50) return '#2e7d32'; // Dark green for very positive
    if (progression > 0) return '#4caf50'; // Green for positive
    if (progression < -50) return '#c62828'; // Dark red for very negative
    if (progression < 0) return '#f44336'; // Red for negative
    return '#757575'; // Gray for neutral
  };

  // Function to get line color based on average progression
  const getLineColor = () => {
    if (!chartData || chartData.length === 0) return getChartColor();
    
    const averageProgression = chartData.reduce((sum, item) => sum + item.progression, 0) / chartData.length;
    
    if (averageProgression > 50) return '#2e7d32'; // Dark green for very positive average
    if (averageProgression > 0) return '#4caf50'; // Green for positive average
    if (averageProgression < -50) return '#c62828'; // Dark red for very negative average
    if (averageProgression < 0) return '#f44336'; // Red for negative average
    return '#757575'; // Gray for neutral average
  };

  // Function to get bar color based on progression
  const getBarColor = (progression: number) => {
    if (progression > 100) return '#1b5e20'; // Very dark green for exceptional
    if (progression > 50) return '#2e7d32'; // Dark green for very positive
    if (progression > 0) return '#4caf50'; // Green for positive
    if (progression < -100) return '#b71c1c'; // Very dark red for very bad
    if (progression < -50) return '#c62828'; // Dark red for very negative
    if (progression < 0) return '#f44336'; // Red for negative
    return '#757575'; // Gray for neutral
  };

  // Function to get performance summary
  const getPerformanceSummary = () => {
    if (!chartData || chartData.length === 0) return null;
    
    const positiveCount = chartData.filter(item => item.progression > 0).length;
    const negativeCount = chartData.filter(item => item.progression < 0).length;
    const totalCount = chartData.length;
    const averageProgression = chartData.reduce((sum, item) => sum + item.progression, 0) / totalCount;
    
    return {
      positiveCount,
      negativeCount,
      totalCount,
      averageProgression,
      positivePercentage: (positiveCount / totalCount) * 100
    };
  };

  const performanceSummary = getPerformanceSummary();

  const renderChart = () => {
    switch (visualizationType) {
      case 'line':
        return (
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis  
              dataKey="name" 
              tick={{ fontSize: 10 }}
              angle={isRTL ? -45 : 45}
              textAnchor="start"
              height={80}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 10 }}
              label={{ value: 'Progression (%)', angle: -90, position: 'insideLeft' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="progression" 
              stroke={getLineColor()} 
              strokeWidth={3}
              dot={(props) => (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={4}
                  fill={getDotColor(props.payload.progression)}
                  stroke={getDotColor(props.payload.progression)}
                  strokeWidth={2}
                />
              )}
              activeDot={(props) => (
                <circle
                  cx={props.cx}
                  cy={props.cy}
                  r={6}
                  fill={getDotColor(props.payload.progression)}
                  stroke={getDotColor(props.payload.progression)}
                  strokeWidth={3}
                />
              )}
              name="Progression %"
            />
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 8 }}
              angle={isRTL ? -45 : 45}
              textAnchor="start"
              height={80}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 10 }}
              label={{ value: 'Progression (%)', angle: -90, position: 'insideLeft' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="progression" 
              radius={[2, 2, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.progression)} />
              ))}
            </Bar>
          </BarChart>
        );

      case 'scatter':
        return (
          <ScatterChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 8 }}
              angle={isRTL ? -45 : 45}
              textAnchor="start"
              height={80}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 10 }}
              label={{ value: 'Progression (%)', angle: -90, position: 'insideLeft' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Scatter 
              dataKey="progression" 
              fill={getChartColor()}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getDotColor(entry.progression)} />
              ))}
            </Scatter>
          </ScatterChart>
        );

      default:
        return null;
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{  fontWeight: 'bold', mb: 0.5 }}>
              {getChartTitle()}
            </Typography>
            {performanceSummary && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {performanceSummary.positiveCount} positive • {performanceSummary.negativeCount} negative • 
                Avg: {performanceSummary.averageProgression > 0 ? '+' : ''}{performanceSummary.averageProgression.toFixed(1)}%
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={visualizationType === 'bar' ? 'contained' : 'outlined'}
              onClick={() => setVisualizationType('bar')}
              color="primary"
              size="small"
            >
              Bar
            </Button>
            <Button
              variant={visualizationType === 'line' ? 'contained' : 'outlined'}
              onClick={() => setVisualizationType('line')}
              color="primary"
              size="small"
            >
              Line
            </Button>
            <Button
              variant={visualizationType === 'scatter' ? 'contained' : 'outlined'}
              onClick={() => setVisualizationType('scatter')}
              color="primary"
              size="small"
            >
              Scatter
            </Button>
          </Box>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('LOADING_CHART_DATA')}
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const AdminOrganizationPage = () => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState('updatedAt');
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [openDeletePopUp, setOpenDeletePopUp] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [menuOrgId, setMenuOrgId] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<any>([]);
  const { setOrganization, setOrganizationId } = React.useContext(UseAuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loadingDownloadCSV, setLoadingDownloadCSV] = useState(false);
  const [period, setPeriod] = useState<'month' | 'quarter' | 'semester' | 'year' | 'week' | 'two_weeks'>('year');
  const [chartType, setChartType] = useState<'project' | 'revision' | 'transaction'>('project');
  
  // Query for table data with pagination - filtered by organization selection
  const {
    data: organizationsData,
    isLoading,
  } = useQuery(
    ['GetOrganizations', page, pageSize, sortOrder, sortBy, selectedOrganizationId, period],
    () => getAdminOrganizationListsOptimized({
      page,
      pageSize,
      sortOrder,
      sortBy,
      search: selectedOrganizationId || null, // Table respects organization filter
      searchType: selectedOrganizationId ? 'organizationId' : null,
      period
    }),
    {
      staleTime: 0, // Refetch immediately when period changes
      cacheTime: 5 * 60 * 1000,
    }
  );

  // Query for chart data with all organizations (limit 100) - unfiltered by organization selection
  const {
    data: chartOrganizationsData,
    isLoading: isChartLoading,
  } = useQuery(
    ['GetOrganizationsForChart', period],
    () => getAdminOrganizationListsOptimized({
      page: 1,
      pageSize: 100, // Get up to 100 organizations for chart
      sortOrder: 'desc',
      sortBy: 'updatedAt',
      search: null, // Charts always show all organizations
      searchType: null, // No filtering for charts
      period
    }),
    {
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
    }
  );


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(newOrderBy);
    setPage(1);
  };

  const handleOrganizationFilter = (organizationId: string | null) => {
    setSelectedOrganizationId(organizationId);
    setPage(1);
  };

  const deleteMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const response = await axiosInstance.delete<any, AxiosResponse<any>>(
        `${API_PATHS.ADMIN_DELETE_ORGANIZATION}/${orgId}`
      );
      return response;
    },
    onSuccess() {
      setOpenDeletePopUp(false);
      queryClient.invalidateQueries(['GetOrganizations']); // Fixed: was 'GetOrganizations'
      enqueueSnackbar(t('DELETE_ORGANIZATION_MESSAGE_SUCCESS'), {
        variant: 'success',
      });
    },
    onError(error: IErrorResponse, variables, context) {
      setOpenDeletePopUp(false);
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
    },
  });

  const deleteOrganizationHandler = async (orgID: any) => {
    try {
      await deleteMutation.mutateAsync(orgID);
    } catch (error: any) { }
  };

  const deleteOrganizationConfirmation = (org: any) => {
    setAnchorEl(org.id);
    setBusinessName(org.company_name)
    setOpenDeletePopUp(true);
  };

  const handleViewOrganizationPage = (e: React.MouseEvent<unknown>, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOrganization(
      organizationsData.data.find((x: any) => x.id == item.id)
    );
    setOrganizationId(item.id);
    navigate(`/admin/organization/${item.id}`);
  }

  const handleClose = () => {
    setOpenDeletePopUp(false);
    setAnchorEl(null);
  };

  const handleCloseMenu = () => {
    setAnchorElMenu(null);
    setMenuOrgId(null);
  };

  const handleDownloadBusinessData = async () => {
    setLoadingDownloadCSV(true);

    try {
      const response = await getOrganizationListsCSV();
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `organization_data_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setLoadingDownloadCSV(false);
    } catch (error) {
      setLoadingDownloadCSV(false);
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    }
  };



  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      
        <OrganizationAutocompleteFilter
          title={t('ADMIN_ORGANIZATION_TITLE')}
          isCreateBusiness={false}
          isLoading={isLoading}
          handleOrganizationFilter={handleOrganizationFilter}
          handleDownloadBusinessData={handleDownloadBusinessData}
          loadingDownloadCSV={loadingDownloadCSV}
          period={period}
          onPeriodChange={setPeriod}
        />
         {/* Chart Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
    
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={chartType === 'project' ? 'contained' : 'outlined'}
            onClick={() => setChartType('project')}
            color="primary"
            size="small"
          >
            {t('ADMIN_STATS_PROJECT_PROGRESSION')}
          </Button>
          <Button
            variant={chartType === 'revision' ? 'contained' : 'outlined'}
            onClick={() => setChartType('revision')}
            color="primary"
            size="small"
          >
            {t('ADMIN_STATS_REVISION_PROGRESSION')}
          </Button>
          <Button
            variant={chartType === 'transaction' ? 'contained' : 'outlined'}
            onClick={() => setChartType('transaction')}
            color="primary"
            size="small"
          >
            {t('ADMIN_STATS_CREDITS_PROGRESSION')}
          </Button>
        </Box>
      </Box>
      </Box>
      
     

      {/* Organization Progression Chart */}
      <OrganizationProgressionChart 
        data={chartOrganizationsData?.data || []}
        chartType={chartType} 
        t={t} 
        isLoading={isChartLoading}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
              <CustomTableFontCell
                sortDirection={sortBy === "index" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "shortId"}
                  direction={sortBy === "shortId" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "shortId")}
                >
                  {t('REVISION_INDEX_COLUMN')}
                  {sortBy === "shortId" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "company_name" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "company_name"}
                  direction={sortBy === "company_name" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "company_name")}
                >
                  {t("ADMIN_DASHBOARD_NAME")}
                  {sortBy === "company_name" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              {/* Project Progression Column */}
              <CustomTableFontCell sortDirection={sortBy === "projectProgression" ? sortOrder : false}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledTableSortLabel
                    active={sortBy === "projectProgression"}
                    direction={sortBy === "projectProgression" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "projectProgression")}
                  >
                    {t("ADMIN_STATS_PROJECT_PROGRESSION")}
                    {sortBy === "projectProgression" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                  <Tooltip 
                    title={`Project creation progression: Last updatedAt Date`}
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </CustomTableFontCell>
              {/* Revision Progression Column */}
              <CustomTableFontCell sortDirection={sortBy === "revisionProgression" ? sortOrder : false}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledTableSortLabel
                    active={sortBy === "revisionProgression"}
                    direction={sortBy === "revisionProgression" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "revisionProgression")}
                  >
                    {t("ADMIN_STATS_REVISION_PROGRESSION")}
                    {sortBy === "revisionProgression" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                  <Tooltip 
                    title={`Revision creation progression: Last updatedAt Date`}
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </CustomTableFontCell>
              {/* Transaction Progression Column */}
              <CustomTableFontCell sortDirection={sortBy === "transactionProgression" ? sortOrder : false}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledTableSortLabel
                    active={sortBy === "transactionProgression"}
                    direction={sortBy === "transactionProgression" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "transactionProgression")}
                  >
                    {t("ADMIN_STATS_CREDITS_PROGRESSION")}
                    {sortBy === "transactionProgression" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                  <Tooltip 
                    title={`Transaction progression: Total Credit Used`}
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </CustomTableFontCell>
              {/* Activity Status Column */}
              <CustomTableFontCell sortDirection={sortBy === "activityStatus" ? sortOrder : false}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StyledTableSortLabel
                    active={sortBy === "activityStatus"}
                    direction={sortBy === "activityStatus" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "activityStatus")}
                  >
                    {t("ADMIN_STATS_ACTIVITY_STATUS")}
                    {sortBy === "activityStatus" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                  <Tooltip 
                    title={`Activity status based on last activity: Active = ≤10 days, Inactive = >10 days since last organization update (project/revision/transaction)`}
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#666', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </CustomTableFontCell>
              <CustomTableFontCell
                sortDirection={sortBy === "updatedAt" ? sortOrder : false}
              >
                <StyledTableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "updatedAt")}
                >
                  {t("ADMIN_DASHBOARD_DATE")}
                  {sortBy === "updatedAt" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell align="right"></CustomTableFontCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizationsData?.data.map((item, index) => (
              <TableRowWithHover
                key={item.id}
                index={(page - 1) * pageSize + index + 1}
                item={item}
                handleViewOrganizationPage={handleViewOrganizationPage}
                setOrganization={setOrganization}
                navigate={navigate}
                data={organizationsData.data}
                t={t}
                setAnchorElMenu={setAnchorElMenu}
                setMenuOrgId={setMenuOrgId}
                menuOrgId={menuOrgId}
                anchorElMenu={anchorElMenu}
                handleCloseMenu={handleCloseMenu}
                deleteOrganizationConfirmation={deleteOrganizationConfirmation}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={organizationsData?.paging.total || 0}
        rowsPerPageOptions={[20]}
        rowsPerPage={pageSize}
        page={page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) =>
          t('MUI_PAGINATION_RANGE', {
            from: from ,
            to: Math.min(to, count),
            count
          })}
      />
      <GenericDialog
        open={openDeletePopUp}
        onClose={handleClose}
        title={`${businessName}`}
        onConfirm={(e) => deleteOrganizationHandler(anchorEl)}
        confirmButtonText={t("DELETE_BUTTON")}
        cancelButtonText={t("CANCEL_BUTTON")}
        isLoading={deleteMutation.isLoading}
      >
        {t("ORGANISATION_DELETE_POPUP_DESCRIPTION")}
      </GenericDialog>
    </Box>
  );
};

export default AdminOrganizationPage;
